import { useCallback, useEffect, useId, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { BarraDeLista, FiltrosAtivos, type FiltroAtivo } from '../../BarraDeLista';
import { Seletor } from '../../Filtros';
import { data, nomeDoContato, plural } from '../../formato';
import { Periodo } from '../../Periodo';
import { useSessao } from '../../sessao';
import { degrauDe, ESTAGIO_DA_OPORTUNIDADE, NIVEL_DO_RADAR } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Carregando, ErroDaApi, RespostaDaAcao, SeloDaEscada } from '../../ui';
import {
  LINHA, MOTIVO_DA_PERDA, reaisRedondos, textoDe, type CartaoDoFunil, type ColunaDoFunil, type Pessoa,
} from './crm';
import { JanelaMover } from './JanelasDaOportunidade';
import './funil.css';

/**
 * O funil — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa 18; spec do
 * Comercial §8.3, a tela aprovada em 17/10/2026). `/comercial/funil`.
 *
 * O quadro em colunas: Novo, Triagem, Em proposta, Enviada, Negociação,
 * Ganha e Perdida (a Descartada fica fora). O cartão anda sozinho pelos
 * eventos do sistema (Tarefa 17); à mão, só com o motivo escrito, que fica
 * no histórico (RN-C02):
 *  · arrastar o cartão (HTML5) para outra coluna abre a janela do motivo com
 *    o estágio da coluna já escolhido; enquanto ela pede o motivo, o cartão
 *    espera na coluna em que caiu, e Cancelar (ou Esc) o devolve;
 *  · «Mover para…» no cartão faz o mesmo pelo teclado e pelo toque;
 *  · a Perdida pede também o motivo da perda (RN-C03).
 * A janela é a mesma da ficha da oportunidade (`JanelaMover`), e a recusa é
 * a do banco, com o que fazer.
 *
 * Cada um vê os seus: a equipe, os pedidos de que cuida e os que indicou; a
 * diretoria, todos, e só ela tem o filtro de responsável. Os outros filtros:
 * a linha e o período (o dia em que o pedido chegou), tudo no endereço
 * (P-LST-05). O de empresa fica para a Etapa 2. O valor é de quem tem o
 * Comercial (`crm.ver`, P2-22). O destaque vem do Radar (Tarefa 19): a
 * proposta parada sem próxima ação ou a tarefa vencida, com o selo do nível
 * (em risco ou crítico) e a frase do que está parado.
 *
 * Ao vivo: `comercial.funil` (só ids; a API confere a carteira a cada
 * evento), como a lista das oportunidades.
 */

/** O tipo do que se arrasta: só o cartão do funil cai numa coluna (um arquivo arrastado, não). */
const TIPO_DO_CARTAO = 'application/x-testesolo-oportunidade';

const PARAMETROS = ['responsavel', 'linha', 'de', 'ate'] as const;

/**
 * O cartão em movimento: de que coluna veio e em que coluna espera (`para`; nulo, na de origem).
 * Arrastado, ele espera na coluna em que caiu — e segue o estágio que a pessoa escolher na janela.
 * Gravado, espera na coluna do estágio gravado até o quadro novo chegar.
 */
interface Movendo { cartao: CartaoDoFunil; de: string; para: string | null; arrastado: boolean; gravado: boolean }

const ha = (dias: number) => (dias <= 0 ? 'desde hoje' : dias === 1 ? 'há 1 dia' : `há ${dias} dias`);

function Cartao({ c, estagio, movendo, aoMover, aoArrastar, aoSoltar }: {
  c: CartaoDoFunil;
  estagio: string;
  movendo: boolean;
  aoMover(): void;
  aoArrastar(id: string): void;
  aoSoltar(): void;
}) {
  const idNome = useId();
  const fechada = estagio === 'ganha' || estagio === 'perdida';
  const valor = c.valorEstimado;
  return (
    <li className={`funil-cartao${movendo ? ' movendo' : ''}${c.destaque ? ` destaque ${c.destaque.nivel}` : ''}`}
      data-id={c.id}
      draggable={c.podeMover}
      aria-busy={movendo || undefined}
      onDragStart={(e) => {
        e.dataTransfer.setData(TIPO_DO_CARTAO, c.id);
        e.dataTransfer.effectAllowed = 'move';
        aoArrastar(c.id);
      }}
      onDragEnd={aoSoltar}>
      <Link className="funil-cartao-nome" id={idNome} to={`/comercial/oportunidade/${c.id}`} draggable={false}>
        {nomeDoContato(c.contato.nome)}
      </Link>
      <span className="funil-cartao-sub">
        {c.cliente?.nome ?? 'ainda sem cliente'}
        {c.cliente?.estrategico && <span className="chip">Estratégico</span>}
      </span>
      <span className="funil-cartao-sub">
        {[c.linha ? textoDe(LINHA, c.linha) : 'linha a identificar', c.municipio ?? 'local não informado'].join(' · ')}
      </span>
      {valor != null && (
        <span className="funil-cartao-valor"
          title={c.valorPelaRegra ? 'Estimativa pela regra simples (os furos e o metro da linha).' : undefined}>
          {c.valorPelaRegra ? `≈ ${reaisRedondos(valor)}` : reaisRedondos(valor)}
        </span>
      )}
      {c.destaque && (
        <span className="funil-cartao-destaque">
          <SeloDaEscada degrau={degrauDe(NIVEL_DO_RADAR, c.destaque.nivel)} /> {c.destaque.motivo}
        </span>
      )}
      {estagio === 'perdida' ? (
        <span className="funil-cartao-sub">{c.motivoPerda ? textoDe(MOTIVO_DA_PERDA, c.motivoPerda) : 'sem o motivo da perda'}</span>
      ) : !fechada && (
        <span className="funil-cartao-sub">
          {c.proximaAcao
            ? `${c.proximaAcao.texto}${c.proximaAcao.em ? ` · ${data(c.proximaAcao.em)}` : ''}`
            : 'sem próxima ação'}
        </span>
      )}
      <span className="funil-cartao-pe">
        <span>{c.responsavel?.nome ?? 'ninguém ainda'}</span>
        <span>{ha(c.diasNoEstagio)}</span>
      </span>
      {c.podeMover && !movendo && (
        <button type="button" className="botao discreto funil-mover" aria-describedby={idNome} onClick={aoMover}>
          Mover para…
        </button>
      )}
    </li>
  );
}

export function Funil() {
  const { pode } = useSessao();
  const diretoria = pode('comercial.diretoria');
  const [busca, setBusca] = useSearchParams();
  const responsavel = diretoria ? busca.get('responsavel') : null;
  const linha = busca.get('linha');
  const de = busca.get('de') ?? '';
  const ate = busca.get('ate') ?? '';
  const [colunas, setColunas] = useState<ColunaDoFunil[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [responsaveis, setResponsaveis] = useState<Pessoa[]>([]);
  const [arrastado, setArrastado] = useState<string | null>(null);
  const [alvo, setAlvo] = useState<string | null>(null);
  const [movendo, setMovendo] = useState<Movendo | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const pedido = useRef(0);
  /** O cartão que ganha o foco quando chegar à coluna nova (I-2 da revisão: o teclado não se perde). */
  const focar = useRef<string | null>(null);

  const mudar = (valores: Partial<Record<(typeof PARAMETROS)[number], string | null>>) => {
    const b = new URLSearchParams(busca);
    for (const [k, v] of Object.entries(valores)) { if (v) b.set(k, v); else b.delete(k); }
    setBusca(b, { replace: true });
  };

  /** Busca o quadro; diz se a resposta foi aplicada, descartada (veio outra depois) ou falhou. */
  const buscar = useCallback(async (): Promise<'aplicado' | 'descartado' | 'falhou'> => {
    const n = ++pedido.current;
    const q = new URLSearchParams();
    if (responsavel) q.set('responsavel', responsavel);
    if (linha) q.set('linha', linha);
    if (de) q.set('de', de);
    if (ate) q.set('ate', ate);
    try {
      const r = await api<{ colunas: ColunaDoFunil[] }>(`/comercial/funil?${q}`);
      if (n !== pedido.current) return 'descartado';
      setColunas(r.colunas);
      setErro(null);
      return 'aplicado';
    } catch (e) {
      if (n !== pedido.current) return 'descartado';
      setErro(e);
      return 'falhou';
    }
  }, [responsavel, linha, de, ate]);

  /*
   * Gravado o movimento, o cartão espera até o primeiro quadro APLICADO depois do «Gravar» (m-2 da
   * revisão): uma resposta descartada não o devolve à origem. Todo quadro buscado depois do
   * «Gravar» já tem o movimento, porque a busca começa depois da resposta da API. Chegando, o foco
   * vai para o cartão na coluna nova.
   */
  useEffect(() => {
    if (!movendo?.gravado) return;
    focar.current = movendo.cartao.id;
    setOk(`O cartão de ${nomeDoContato(movendo.cartao.contato.nome)} andou.`);
    setMovendo(null);
  }, [colunas]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!focar.current || movendo) return;
    const cartao = document.querySelector(`.funil-cartao[data-id="${focar.current}"]`);
    focar.current = null;
    (cartao?.querySelector<HTMLElement>('.funil-mover') ?? cartao?.querySelector<HTMLElement>('.funil-cartao-nome'))?.focus();
  });

  useEffect(() => { void buscar(); }, [buscar]);
  useEffect(() => {
    if (!diretoria) return;
    api<Pessoa[]>('/comercial/oportunidades/responsaveis').then(setResponsaveis).catch(() => setResponsaveis([]));
  }, [diretoria]);

  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(['comercial.funil'], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 300);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  /* O quadro como a tela mostra: o cartão arrastado espera na coluna em que caiu até a janela responder. */
  const quadro = useMemo(() => {
    if (!colunas) return null;
    if (!movendo?.para || movendo.para === movendo.de) return colunas;
    const { cartao, de: origem, para } = movendo;
    /* A conta e a soma andam com o cartão: senão a coluna de origem diria «Mostrando 2 de 3» sem ter cortado nada. */
    const valor = cartao.valorEstimado ?? 0;
    const soma = (s: number | undefined, d: number) => (s === undefined ? undefined : s + d);
    return colunas.map((c) => (
      c.estagio === origem
        ? { ...c, total: c.total - 1, soma: soma(c.soma, -valor), cartoes: c.cartoes.filter((x) => x.id !== cartao.id) }
        : c.estagio === para
          ? { ...c, total: c.total + 1, soma: soma(c.soma, valor), cartoes: [cartao, ...c.cartoes.filter((x) => x.id !== cartao.id)] }
          : c));
  }, [colunas, movendo]);

  const acharCartao = (id: string) => {
    for (const c of colunas ?? []) {
      const x = c.cartoes.find((k) => k.id === id);
      if (x) return { cartao: x, de: c.estagio };
    }
    return null;
  };

  const soltarEm = (e: DragEvent, estagio: string) => {
    e.preventDefault();
    setAlvo(null);
    const id = e.dataTransfer.getData(TIPO_DO_CARTAO) || arrastado;
    setArrastado(null);
    const achado = id ? acharCartao(id) : null;
    if (!achado || achado.de === estagio || !achado.cartao.podeMover) return;
    setOk(null);
    setMovendo({ ...achado, para: estagio, arrastado: true, gravado: false });
  };

  const aceita = (e: DragEvent) => e.dataTransfer.types.includes(TIPO_DO_CARTAO);

  const filtrosAtivos: FiltroAtivo[] = [
    ...(responsavel ? [{ chave: 'responsavel',
      texto: `Responsável: ${responsaveis.find((p) => p.id === responsavel)?.nome ?? '…'}`,
      tirar: () => mudar({ responsavel: null }) }] : []),
    ...(linha ? [{ chave: 'linha', texto: `Linha: ${textoDe(LINHA, linha)}`, tirar: () => mudar({ linha: null }) }] : []),
    ...(de || ate ? [{ chave: 'periodo',
      texto: `Chegaram ${de && ate ? `de ${data(de)} a ${data(ate)}` : de ? `desde ${data(de)}` : `até ${data(ate)}`}`,
      tirar: () => mudar({ de: null, ate: null }) }] : []),
  ];
  const total = (colunas ?? []).reduce((s, c) => s + c.total, 0);

  return (
    <div className="funil-tela">
      <header className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Funil</h1>
          <p className="sub">
            {diretoria ? 'Todos os pedidos do Comercial.' : 'Os pedidos de que você cuida e os que você indicou.'}
            {' '}Cada coluna começa pelo cartão parado há mais tempo; Ganha e Perdida, pelos mais recentes.
          </p>
        </div>
        <Link className="botao secundario" to="/comercial/oportunidades">Ver em lista</Link>
      </header>

      <BarraDeLista rotulo="Filtros do funil" ativos={filtrosAtivos.length} total={colunas ? total : undefined}
        aoLimpar={() => setBusca(new URLSearchParams(), { replace: true })}>
        {diretoria && (
          <Seletor naBarra rotulo="Responsável" valor={responsavel} todos="Todos"
            opcoes={responsaveis.map((p) => ({ valor: p.id, texto: p.nome }))} aoMudar={(v) => mudar({ responsavel: v })} />
        )}
        <Seletor naBarra rotulo="Linha" valor={linha} todos="Todas as linhas"
          opcoes={Object.entries(LINHA).map(([valor, t]) => ({ valor, texto: t }))} aoMudar={(v) => mudar({ linha: v })} />
        <Periodo valor={{ de, ate }} rotulo="Chegaram" atalhos={['semana', 'mes', 'mesPassado', 'ultimos30', 'tudo']}
          aoMudar={(v) => mudar({ de: v.de || null, ate: v.ate || null })} />
      </BarraDeLista>
      <FiltrosAtivos itens={filtrosAtivos} aoLimpar={() => setBusca(new URLSearchParams(), { replace: true })}
        contagem={colunas ? `${plural(total, 'oportunidade', 'oportunidades')} no quadro` : undefined} />
      <AtualizacaoPausada estado={tempoReal} />
      <RespostaDaAcao erro={null} ok={ok} />

      {erro && !quadro ? <ErroDaApi erro={erro} />
        : !quadro ? <Carregando texto="Carregando o funil" />
          : (
            <div className="funil-quadro" role="region" aria-label="O quadro do funil" tabIndex={0}>
              {quadro.map((col) => {
                const nome = degrauDe(ESTAGIO_DA_OPORTUNIDADE, col.estagio).texto;
                const idTitulo = `funil-coluna-${col.estagio}`;
                return (
                  <section key={col.estagio} className={`funil-coluna${alvo === col.estagio ? ' alvo' : ''}`}
                    data-estagio={col.estagio} aria-labelledby={idTitulo}
                    onDragEnter={(e) => { if (aceita(e)) { e.preventDefault(); setAlvo(col.estagio); } }}
                    onDragOver={(e) => { if (aceita(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setAlvo((a) => (a === col.estagio ? null : a));
                    }}
                    onDrop={(e) => soltarEm(e, col.estagio)}>
                    <header className="funil-coluna-cabeca">
                      <h2 id={idTitulo}>{nome}</h2>
                      <span className="funil-coluna-resumo">
                        {col.total}
                        {col.soma ? ` · ${reaisRedondos(col.soma)}` : ''}
                      </span>
                    </header>
                    {col.cartoes.length === 0 ? (
                      <p className="funil-coluna-vazia">nenhuma oportunidade aqui</p>
                    ) : (
                      <ol className="funil-cartoes">
                        {col.cartoes.map((c) => (
                          /* Só o cartão arrastado fica «em movimento»: o aberto pelo botão guarda o
                             botão, para o foco voltar a ele quando a janela fecha. */
                          <Cartao key={c.id} c={c} estagio={col.estagio}
                            movendo={movendo?.cartao.id === c.id && movendo.arrastado}
                            aoMover={() => {
                              setOk(null);
                              setMovendo({ cartao: c, de: col.estagio, para: null, arrastado: false, gravado: false });
                            }}
                            aoArrastar={setArrastado}
                            aoSoltar={() => { setArrastado(null); setAlvo(null); }} />
                        ))}
                      </ol>
                    )}
                    {col.total > col.cartoes.length && (
                      <p className="funil-coluna-corte">
                        Mostrando {col.cartoes.length} de {col.total}. Use os filtros para achar as outras.
                      </p>
                    )}
                  </section>
                );
              })}
            </div>
          )}

      <JanelaMover aberta={!!movendo && !movendo.gravado}
        oportunidade={movendo
          ? { id: movendo.cartao.id, estagio: movendo.de, contato: nomeDoContato(movendo.cartao.contato.nome) }
          : { id: '', estagio: '', contato: '' }}
        para={movendo?.para ?? null}
        /* Arrastado, o cartão que espera segue o estágio escolhido na janela (m-1 da revisão). Aberto
           pelo botão, fica onde está: o botão guarda o foco para quando a janela fechar. */
        aoEscolherPara={(p) => setMovendo((m) => (m?.arrastado ? { ...m, para: p } : m))}
        aoFechar={() => setMovendo(null)}
        aoMover={(para) => {
          /* A janela fecha já; o cartão espera na coluna do estágio gravado até o quadro novo chegar. */
          setMovendo((m) => (m ? { ...m, para, gravado: true } : m));
          void buscar().then((r) => {
            /* Sem quadro novo, o cartão não fica esperando para sempre: volta ao quadro que se tem. */
            if (r === 'falhou') setMovendo(null);
          });
        }} />
    </div>
  );
}
